import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { 
  Home, 
  ShoppingBag, 
  Users, 
  UsersRound, 
  MessageCircle, 
  Church, 
  Bookmark, 
  Heart, 
  Cross, 
  Building2, 
  Shield, 
  ChevronDown, 
  ChevronUp,
  Package,
  BarChart3,
  UserPlus,
  Ticket,
  Search,
  Check,
  Globe,
  Newspaper,
  Star,
  BookOpen,
  ListChecks,
  Briefcase
} from "lucide-react";
import { MapPinned, HandHeart } from "lucide-react";
import { HaloIcon } from "@/components/icons/HaloIcon";
import { resolveImageUrl } from "@/lib/api";
import HabitWidget from "@/components/HabitWidget";

export default function Sidebar({ user }) {
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);
  const [showParishSelector, setShowParishSelector] = useState(false);
  const [parishSearch, setParishSearch] = useState("");
  const parishSelectorRef = useRef(null);
  
  // Get user's parishes - support both single parish and multiple parishes
  const userParishes = user?.parishes || (user?.parish_id ? [{
    parish_id: user.parish_id,
    name: user.parish_name || "My Parish"
  }] : []);
  
  const hasMultipleParishes = userParishes.length > 1;
  const currentParish = userParishes.find(p => p.parish_id === user?.parish_id) || userParishes[0];
  
  // Filter parishes based on search
  const filteredParishes = userParishes.filter(p => 
    p.name?.toLowerCase().includes(parishSearch.toLowerCase())
  );
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (parishSelectorRef.current && !parishSelectorRef.current.contains(event.target)) {
        setShowParishSelector(false);
        setParishSearch("");
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");
  
  const mainLinks = [
    { path: "/dashboard", icon: Home, label: "Home" },
    { path: "/marketplace", icon: ShoppingBag, label: "Marketplace" },
    { path: "/directory", icon: Building2, label: "Business Directory" },
    { path: "/groups", icon: UsersRound, label: "Groups" },
    { path: "/community", icon: Users, label: "Community" },
    { path: "/sacrament-finder", icon: MapPinned, label: "Sacrament Finder" },
    { path: "/requests", icon: HandHeart, label: "Offers & Requests" },
  ];
  
  const moreLinks = [
    { path: "/my-rule-of-life", icon: ListChecks, label: "Rule of Life" },
    { path: "/saint-of-the-day", icon: HaloIcon, label: "Saint of the Day" },
  ];
  
  const sellerLinks = user?.is_seller ? [
    { path: "/seller", icon: BarChart3, label: "Seller Dashboard" },
    { path: "/my-orders", icon: Package, label: "My Orders" },
  ] : [];
  
  const adminLinks = [];
  if (user?.is_site_admin || user?.role === "site_admin" || user?.role === "admin") {
    adminLinks.push({ path: "/admin", icon: Shield, label: "Site Admin" });
    // Site admins also get access to parish admin tools
    adminLinks.push({ path: "/parish-admin", icon: Church, label: "Parish Admin" });
  } else if (user?.is_parish_admin) {
    adminLinks.push({ path: "/parish-admin", icon: Church, label: "Parish Admin" });
  }

  const NavItem = ({ link }) => (
    <Link
      to={link.path}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all font-['Nunito'] text-sm
        ${isActive(link.path) 
          ? "bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] font-semibold" 
          : "text-[#2D2A26] hover:bg-[#F0EBE0]"
        }`}
      data-testid={`sidebar-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <link.icon className={`w-5 h-5 ${isActive(link.path) ? "text-[var(--liturgical-primary)]" : "text-[#6B665E]"}`} />
      <span>{link.label}</span>
    </Link>
  );

  return (
    <aside className="hidden lg:flex flex-col w-[280px] h-[calc(100vh-56px)] sticky top-14 bg-white border-r border-[#E5E0D5] overflow-y-auto">
      <div className="p-4 space-y-1">
        {/* User Profile */}
        <Link 
          to={`/profile/${user?.user_id}`}
          className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#F0EBE0] transition-all"
          data-testid="sidebar-profile"
        >
          <Avatar className="h-9 w-9 border border-[#E5E0D5]">
            <AvatarImage src={resolveImageUrl(user?.picture)} />
            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] text-sm">
              {user?.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="font-['Nunito'] font-semibold text-[#2D2A26]">{user?.name}</span>
        </Link>
        
        {/* Main Navigation */}
        <div className="pt-2 space-y-0.5">
          {mainLinks.map((link) => (
            <NavItem key={link.path} link={link} />
          ))}
        </div>
        
        {/* See More Section */}
        <button
          onClick={() => setShowMore(!showMore)}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left hover:bg-[#F0EBE0] transition-all font-['Nunito'] text-sm text-[#2D2A26]"
          data-testid="sidebar-see-more"
        >
          <div className="w-5 h-5 rounded-full bg-[#F0EBE0] flex items-center justify-center">
            {showMore ? <ChevronUp className="w-3.5 h-3.5 text-[#6B665E]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#6B665E]" />}
          </div>
          <span>{showMore ? "See less" : "See more"}</span>
        </button>
        
        {showMore && (
          <div className="space-y-0.5 animate-in slide-in-from-top-2 duration-200">
            {moreLinks.map((link) => (
              <NavItem key={link.path} link={link} />
            ))}
            {sellerLinks.map((link) => (
              <NavItem key={link.path} link={link} />
            ))}
          </div>
        )}
        
        {/* Admin Links */}
        {adminLinks.length > 0 && (
          <>
            <div className="border-t border-[#E5E0D5] my-3" />
            <p className="px-3 text-xs font-['Nunito'] text-[#6B665E] uppercase tracking-wide mb-2">Admin</p>
            <div className="space-y-0.5">
              {adminLinks.map((link) => (
                <NavItem key={link.path} link={link} />
              ))}
            </div>
          </>
        )}
        
        {/* Your Parishes Section */}
        {userParishes.length > 0 && (
          <>
            <div className="border-t border-[#E5E0D5] my-3" />
            <p className="px-3 text-xs font-['Nunito'] text-[#6B665E] uppercase tracking-wide mb-2">
              {hasMultipleParishes ? "Your Parishes" : "Your Parish"}
            </p>
            
            {hasMultipleParishes ? (
              <div className="relative px-2" ref={parishSelectorRef}>
                {/* Current Parish / Dropdown Trigger */}
                <button
                  onClick={() => setShowParishSelector(!showParishSelector)}
                  className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-[#F0EBE0] transition-all text-left"
                  data-testid="parish-selector-trigger"
                >
                  <div className="w-9 h-9 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center flex-shrink-0">
                    <Church className="w-5 h-5 text-[var(--liturgical-primary)]" />
                  </div>
                  <span className="font-['Nunito'] text-sm text-[#2D2A26] line-clamp-1 flex-1">
                    {currentParish?.name || "Select Parish"}
                  </span>
                  {showParishSelector ? (
                    <ChevronUp className="w-4 h-4 text-[#6B665E] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[#6B665E] flex-shrink-0" />
                  )}
                </button>
                
                {/* Dropdown Panel */}
                {showParishSelector && (
                  <div className="absolute left-2 right-2 mt-1 bg-white rounded-lg border border-[#E5E0D5] shadow-lg z-50 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-[#E5E0D5]">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                        <Input
                          value={parishSearch}
                          onChange={(e) => setParishSearch(e.target.value)}
                          placeholder="Search parishes..."
                          className="pl-8 h-8 text-sm border-[#E5E0D5] font-['Nunito']"
                          data-testid="parish-search-input"
                          autoFocus
                        />
                      </div>
                    </div>
                    
                    {/* Parish List */}
                    <div className="max-h-48 overflow-y-auto">
                      {filteredParishes.length === 0 ? (
                        <p className="p-3 text-sm text-[#6B665E] font-['Nunito'] text-center">
                          No parishes found
                        </p>
                      ) : (
                        filteredParishes.map((parish) => (
                          <Link
                            key={parish.parish_id}
                            to={`/parish/${parish.parish_id}`}
                            onClick={() => {
                              setShowParishSelector(false);
                              setParishSearch("");
                            }}
                            className={`flex items-center gap-3 px-3 py-2.5 hover:bg-[#F0EBE0] transition-colors ${
                              parish.parish_id === currentParish?.parish_id ? 'bg-[#F0EBE0]' : ''
                            }`}
                            data-testid={`parish-option-${parish.parish_id}`}
                          >
                            <Church className="w-4 h-4 text-[var(--liturgical-primary)] flex-shrink-0" />
                            <span className="font-['Nunito'] text-sm text-[#2D2A26] line-clamp-1 flex-1">
                              {parish.name}
                            </span>
                            {parish.parish_id === currentParish?.parish_id && (
                              <Check className="w-4 h-4 text-[var(--liturgical-primary)] flex-shrink-0" />
                            )}
                          </Link>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to={`/parish/${user.parish_id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F0EBE0] transition-all"
                data-testid="sidebar-my-parish"
              >
                <div className="w-9 h-9 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                  <Church className="w-5 h-5 text-[var(--liturgical-primary)]" />
                </div>
                <span className="font-['Nunito'] text-sm text-[#2D2A26] line-clamp-1">{user.parish_name || "My Parish"}</span>
              </Link>
            )}
          </>
        )}
      </div>

      {/* Habit Widget */}
      <div className="px-3 mt-3">
        <HabitWidget />
      </div>
      
      {/* Footer */}
      <div className="mt-auto p-4 border-t border-[#E5E0D5]">
        <p className="text-xs font-['Nunito'] text-[#6B665E] text-center">
          Parish · Connecting the faithful
        </p>
      </div>
    </aside>
  );
}
